import React, { createContext, useContext, useState, useEffect } from 'react';
import { sanitizePathForPrivacy } from '../lib/utils';

interface PrivacyContextType {
  isPrivacyMode: boolean;
  togglePrivacyMode: () => void;
  displayPath: (rawPath: string) => string;
  deviceLabel: string;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

export const PrivacyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPrivacyMode, setIsPrivacyMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('kaizen_privacy_mode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('kaizen_privacy_mode', JSON.stringify(isPrivacyMode));
    document.title = isPrivacyMode ? 'KAIZEN Workspace' : 'KAIZEN Remote';
  }, [isPrivacyMode]);

  // Keyboard shortcut: Alt + P
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setIsPrivacyMode(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const togglePrivacyMode = () => setIsPrivacyMode(prev => !prev);

  const displayPath = (rawPath: string) => {
    return sanitizePathForPrivacy(rawPath, isPrivacyMode);
  };

  const deviceLabel = isPrivacyMode ? 'Workspace Node' : 'Personal Laptop';

  return (
    <PrivacyContext.Provider value={{ isPrivacyMode, togglePrivacyMode, displayPath, deviceLabel }}>
      {children}
    </PrivacyContext.Provider>
  );
};

export const usePrivacy = (): PrivacyContextType => {
  const ctx = useContext(PrivacyContext);
  if (!ctx) throw new Error('usePrivacy must be used within a PrivacyProvider');
  return ctx;
};
